import { useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'

interface InteractiveDotsProps {
  className?: string
  /** Grid pitch in CSS pixels. */
  spacing?: number
  /** Largest square side in CSS pixels. */
  maxSize?: number
  /** Dot colour as an "r,g,b" triple; white suits the coloured bands. */
  rgb?: string
  /** Multiplier on dot opacity (lighter grounds want stronger dots). */
  intensity?: number
  /**
   * Wave and pointer halo. Off by default: a single still frame. Only the
   * landing page turns it on; elsewhere the motion was distracting.
   */
  interactive?: boolean
}

const POINTER_RADIUS = 170
const PUSH = 10

const smoothstep = (a: number, b: number, x: number) => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)))
  return t * t * (3 - 2 * t)
}

/**
 * Halftone dot field for the coloured bands (the reference's animated dots):
 * square dots that thicken toward the right of the band, a slow travelling
 * wave so the field keeps changing, and a pointer halo where dots grow,
 * brighten and part around the cursor before easing back.
 *
 * Canvas, white at low alpha, so it reads on blue, purple and the gradient
 * alike. Animates only while on screen; with reduced motion it draws one
 * static frame and ignores the pointer. Decorative: aria-hidden and
 * pointer-events none (the pointer is tracked on the parent band).
 */
export function InteractiveDots({
  className,
  spacing = 16,
  maxSize = 4.2,
  rgb = '255,255,255',
  intensity = 1,
  interactive = false,
}: InteractiveDotsProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    const host = canvas?.parentElement
    const ctx = canvas?.getContext('2d')
    if (!canvas || !host || !ctx) return

    // "still" covers both reduced motion and non-interactive fields.
    const reduced = !interactive || window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches

    let width = 0
    let height = 0
    let raf = 0
    let visible = false
    // Pointer target and an eased copy, plus an eased "presence" so the halo
    // fades in and out instead of popping.
    const pointer = { x: -9999, y: -9999, inside: false }
    const eased = { x: -9999, y: -9999, presence: 0 }

    const resize = () => {
      const rect = host.getBoundingClientRect()
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      width = rect.width
      height = rect.height
      canvas.width = Math.round(width * dpr)
      canvas.height = Math.round(height * dpr)
      canvas.style.width = `${width}px`
      canvas.style.height = `${height}px`
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }

    const draw = (time: number) => {
      ctx.clearRect(0, 0, width, height)
      const t = reduced ? 0 : time / 1000
      eased.x += (pointer.x - eased.x) * 0.18
      eased.y += (pointer.y - eased.y) * 0.18
      eased.presence += ((pointer.inside ? 1 : 0) - eased.presence) * 0.08

      const cols = Math.ceil(width / spacing) + 1
      const rows = Math.ceil(height / spacing) + 1
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const x = c * spacing
          const y = r * spacing
          // Density: thickens from 30% to 80% of the width, with a slow
          // diagonal wave travelling through it.
          const ramp = smoothstep(0.3, 0.8, x / width)
          const wave = 0.5 + 0.5 * Math.sin(x * 0.012 + y * 0.018 - t * 0.6)
          let size = maxSize * (0.18 + 0.82 * ramp) * (0.55 + 0.45 * wave)
          let alpha = 0.1 + 0.22 * ramp

          let dx = 0
          let dy = 0
          if (eased.presence > 0.01) {
            const ox = x - eased.x
            const oy = y - eased.y
            const dist = Math.hypot(ox, oy)
            if (dist < POINTER_RADIUS) {
              const k = (1 - dist / POINTER_RADIUS) ** 2 * eased.presence
              size = size + (maxSize * 1.6 - size) * k
              alpha = alpha + (0.75 - alpha) * k
              if (dist > 0.001) {
                dx = (ox / dist) * PUSH * k
                dy = (oy / dist) * PUSH * k
              }
            }
          }

          if (size < 0.35) continue
          ctx.fillStyle = `rgba(${rgb},${Math.min(1, alpha * intensity).toFixed(3)})`
          ctx.fillRect(x + dx - size / 2, y + dy - size / 2, size, size)
        }
      }
    }

    const loop = (time: number) => {
      draw(time)
      if (visible && !reduced) raf = requestAnimationFrame(loop)
    }

    const start = () => {
      cancelAnimationFrame(raf)
      if (reduced) draw(0)
      else raf = requestAnimationFrame(loop)
    }

    const onMove = (e: PointerEvent) => {
      const rect = host.getBoundingClientRect()
      pointer.x = e.clientX - rect.left
      pointer.y = e.clientY - rect.top
      if (!pointer.inside) {
        pointer.inside = true
        // Start the halo at the cursor rather than sliding in from offscreen.
        if (eased.presence < 0.05) {
          eased.x = pointer.x
          eased.y = pointer.y
        }
      }
    }
    const onLeave = () => {
      pointer.inside = false
    }

    resize()
    const ro = new ResizeObserver(() => {
      resize()
      if (reduced) draw(0)
    })
    ro.observe(host)

    const io = new IntersectionObserver(([entry]) => {
      visible = Boolean(entry?.isIntersecting)
      if (visible) start()
      else cancelAnimationFrame(raf)
    })
    io.observe(host)

    if (finePointer && !reduced) {
      host.addEventListener('pointermove', onMove)
      host.addEventListener('pointerleave', onLeave)
    }

    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
      io.disconnect()
      host.removeEventListener('pointermove', onMove)
      host.removeEventListener('pointerleave', onLeave)
    }
  }, [spacing, maxSize, rgb, intensity, interactive])

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className={cn('pointer-events-none absolute inset-0 z-0', className)}
    />
  )
}

export default InteractiveDots
